import React from 'react';
import { skeletonStyle, skeletonTextStyle } from './styles';
import { toDomStyle } from '@/util/domStyle';

import type { VariantProps } from '@gluestack-ui/nativewind-utils';

type ISkeletonProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof skeletonStyle> & {
    startColor?: string;
    isLoaded?: boolean;
  };

/** Tailwind's `animate-pulse` cycle. */
const PULSE_CYCLE_MS = 2000;

/**
 * A negative delay that starts the CSS pulse partway through its cycle, at the
 * point the clock is in now, so every skeleton pulses together and one that
 * replaces another mid-load carries on the same beat instead of restarting.
 */
function getPulseDelay() {
  return `-${Date.now() % PULSE_CYCLE_MS}ms`;
}

const Skeleton = React.forwardRef<HTMLDivElement, ISkeletonProps>(
  (
    {
      className,
      variant = 'rounded',
      children,
      speed = 2,
      startColor = 'bg-background-200',
      isLoaded = false,
      ...props
    },
    ref
  ) => {
    const [animationDelay] = React.useState(getPulseDelay);

    if (!isLoaded) {
      return (
        <div
          ref={ref}
          className={`motion-safe:animate-pulse ${startColor} ${skeletonStyle({
            variant,
            speed,
            class: className,
          })}`}
          {...props}
          style={{ animationDelay, ...toDomStyle(props.style) }}
        />
      );
    } else {
      return children;
    }
  }
);

type ISkeletonTextProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof skeletonTextStyle> & {
    _lines?: number;
    isLoaded?: boolean;
    startColor?: string;
  };

const SkeletonText = React.forwardRef<HTMLDivElement, ISkeletonTextProps>(
  (
    {
      className,
      _lines,
      isLoaded = false,
      startColor = 'bg-background-200',
      gap = 2,
      children,
      ...props
    },
    ref
  ) => {
    const [animationDelay] = React.useState(getPulseDelay);

    if (!isLoaded) {
      if (_lines) {
        return (
          <div
            ref={ref}
            className={`flex flex-col ${skeletonTextStyle({
              gap,
            })}`}
          >
            {Array.from({ length: _lines }).map((_, index) => (
              <div
                key={index}
                className={`motion-safe:animate-pulse ${startColor} ${skeletonTextStyle({
                  class: className,
                })}`}
                {...props}
                style={{ animationDelay, ...toDomStyle(props.style) }}
              />
            ))}
          </div>
        );
      } else {
        return (
          <div
            ref={ref}
            className={`motion-safe:animate-pulse ${startColor} ${skeletonTextStyle({
              class: className,
            })}`}
            {...props}
            style={{ animationDelay, ...toDomStyle(props.style) }}
          />
        );
      }
    } else {
      return children;
    }
  }
);

Skeleton.displayName = 'Skeleton';
SkeletonText.displayName = 'SkeletonText';

export { Skeleton, SkeletonText };
